import AssessmentIcon from "@mui/icons-material/Assessment"
import CancelIcon from "@mui/icons-material/Cancel"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import {
    AlertTitle,
    Table,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { ReactElement, useEffect } from "react"

import {
    TT_ENA_CHECK_FAIL,
    TT_ENA_CHECK_PASS,
    TT_ENA_CHECK_WARNING,
} from "../../../constants/tooltips"
import { SUPPORT_REFER_ENTITY } from "../../../errors/errorMessages"
import { formatDateTime, snackbar } from "../../../utils/general/generalUtils"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import AppTooltip from "../../helper/appTooltip"
import { AppCard, AppCardContent, AppCardHeader } from "../../surfaces/appCard"
import AppTableContentRow from "../../tables/appTableContentRow"
import DisplayIf from "../../wrappers/displayIf"
import { formatKeys } from "../singleApplicationKeys"

const FAILURE_MSG_PT_1 =
    "Connect Direct encountered an issue whilst assessing this application. As a precaution, the application was escalated to you to review"
const FAILURE_MSG_PT_2 = `You can continue to review and respond to this application like any other without impact to the installer or customer`
const FAILURE_MSG_PT_3 = `Please report this application ID to the ${SUPPORT_REFER_ENTITY} so we can investigate`

type TEnaAssessment = Record<string, boolean | string>
interface IEnaAssessmentProps {
    enaAssessment: TEnaAssessment
}

/** Labels shown here get an amber "warning" icon instead of a red "fail"
 * icon when their value is false. Add/remove labels to change this.
 */
const ENA_ASSESSMENT_WARNING_LABELS = new Set<string>(["Cut-Out is Modern?"])

/**
 * Determines the icon and tooltip shown for a single assessment criterion.
 * @param { string } label - the formatted, human-readable criterion label
 * @param { boolean | string } value - the value of the assessment field
 * @returns { ReactElement } - the icon + tooltip to render for this outcome
 */
const getAssessmentOutcome = (
    label: string,
    value: boolean | string
): ReactElement => {
    if (value === true) {
        return (
            <AppTooltip title={TT_ENA_CHECK_PASS}>
                <CheckCircleIcon color="success" />
            </AppTooltip>
        )
    }

    if (ENA_ASSESSMENT_WARNING_LABELS.has(label)) {
        return (
            <AppTooltip title={TT_ENA_CHECK_WARNING}>
                <CheckCircleIcon color="warning" />
            </AppTooltip>
        )
    }

    return (
        <AppTooltip title={TT_ENA_CHECK_FAIL}>
            <CancelIcon color="error" />
        </AppTooltip>
    )
}

/**
 * Displays a checkbox table showing output of ENA assessment. This is schema
 * agnostic, only assuming the assessment is of type `IEnaAssessmentProps`
 */
const EnaAssessment = ({
    enaAssessment,
}: IEnaAssessmentProps): ReactElement => {
    const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        enaPlatformDecision,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        thresholdComment,
        decisionTimestampUTC,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        thresholdsUsed,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        note,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        cutoutType,
        ...assessment
    } = enaAssessment

    const enaProcessingSuccessful =
        enaAssessment?.enaProcessingSuccessful ?? true

    // enaProcessingSuccessful is static so this useEffect will only run once
    useEffect(() => {
        if (enaProcessingSuccessful === false) {
            snackbar(`${FAILURE_MSG_PT_1}. ${FAILURE_MSG_PT_3}`, "warning")
        }
    }, [enaProcessingSuccessful])

    const formattedAssessment = formatKeys(assessment)
    return (
        <AppCard>
            <AppCardHeader
                avatar={<AssessmentIcon fontSize="large" />}
                title="Connect Direct Assessment"
                subheader={enaPlatformDecision}
                titleTypographyProps={{
                    variant: "h5",
                    fontWeight: "500",
                }}
            />
            <AppCardContent>
                <DisplayIf condition={enaProcessingSuccessful === false}>
                    <AppAlert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>
                            We had an issue assessing this one!
                        </AlertTitle>
                        {`${FAILURE_MSG_PT_1}. ${FAILURE_MSG_PT_2} . ${FAILURE_MSG_PT_3}`}
                    </AppAlert>
                </DisplayIf>
                <Table size="small">
                    <TableHead>
                        <TableRow hover>
                            <TableCell>Assessment Criteria</TableCell>
                            <TableCell align="center">Outcome</TableCell>
                        </TableRow>
                        {Object.keys(formattedAssessment).map((key: string) => (
                            <AppTableContentRow key={key} hover>
                                <TableCell>{key}</TableCell>
                                <TableCell align="center">
                                    {getAssessmentOutcome(
                                        key,
                                        formattedAssessment[key]
                                    )}
                                </TableCell>
                            </AppTableContentRow>
                        ))}
                        <TableRow>
                            <TableCell>Cut-Out Type</TableCell>
                            <TableCell align="center">{cutoutType}</TableCell>
                        </TableRow>
                        {decisionTimestampUTC && (
                            <AppTableContentRow hover>
                                <TableCell>Assessment Timestamp</TableCell>
                                <TableCell align="center">
                                    {formatDateTime(decisionTimestampUTC)}
                                </TableCell>
                            </AppTableContentRow>
                        )}
                    </TableHead>
                </Table>
                <AppAlert
                    severity="info"
                    sx={{ mt: 2, borderRadius: 2, boxShadow: 3 }}
                >
                    For an application to be auto-approved, all Assessment
                    Criteria checks must pass.
                </AppAlert>
                {thresholdComment && (
                    <Typography marginTop={2} marginRight={1} variant="body2">
                        <h4>Threshold Comment</h4>
                        {thresholdComment}
                    </Typography>
                )}
            </AppCardContent>
        </AppCard>
    )
}

export default EnaAssessment
